import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Map, { Marker, FullscreenControl } from 'react-map-gl/mapbox';
import useSupercluster from 'use-supercluster';
import markerIcon from '../../assets/img/marker.png';
import { useThemeMode } from '../../theme/ThemeProvider';
import 'mapbox-gl/dist/mapbox-gl.css';
import styles from './MapboxMap.module.scss';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;
const ICELAND_CENTER = { longitude: -21.93, latitude: 64.14 };
const DEFAULT_ZOOM = 10;
const MAP_STYLE_LIGHT = 'mapbox://styles/mapbox/streets-v12';
const MAP_STYLE_DARK = 'mapbox://styles/mapbox/dark-v11';

const companyLocationDeepLink = (companyId, locationId) => {
    if (!companyId) return null;
    const params = new URLSearchParams();
    params.set('step', 'locations');
    if (locationId != null && locationId !== '') {
        params.set('location', String(locationId));
    }
    return `/company/${companyId}?${params.toString()}`;
};

export default function MapboxMap({ companies }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { isDark } = useThemeMode();
    const mapRef = useRef();
    const timeoutRef = useRef();
    const [viewState, setViewState] = useState({
        ...ICELAND_CENTER,
        zoom: DEFAULT_ZOOM,
    });
    const [bounds, setBounds] = useState(null);
    const [zoom, setZoom] = useState(DEFAULT_ZOOM);
    const [activeMarker, setActiveMarker] = useState(null);
    const [zoomEnabled, setZoomEnabled] = useState(false);
    const [showZoomMsg, setShowZoomMsg] = useState(false);
    const [mapLoaded, setMapLoaded] = useState(false);

    // Suppress the pointRayIntersection TypeError that mapbox-gl v3 throws
    // on mouse events during initialization before internal camera state is ready.
    useEffect(() => {
        const handleError = (event) => {
            if (
                event.error?.message?.includes('r is undefined') ||
                event.message?.includes('r is undefined')
            ) {
                event.stopImmediatePropagation();
                event.preventDefault();
            }
        };
        window.addEventListener('error', handleError, true);
        return () => window.removeEventListener('error', handleError, true);
    }, []);

    const points =
        companies &&
        companies.map((company) => {
            const latitude = parseFloat(company.latitude || '0');
            const longitude = parseFloat(company.longitude || '0');
            return {
                type: 'Feature',
                properties: {
                    cluster: false,
                    locationId: company.id,
                    companyDbId: company.torgid_company_id || company.torgid_company?.id || null,
                    address: company.address || '',
                    locationName: company.name || '',
                    description: company.description || '',
                    phone: company.phone || '',
                    logo: company.torgid_company?.logo || null,
                    cName: company.torgid_company?.name || '',
                    latitude,
                    longitude,
                },
                geometry: {
                    type: 'Point',
                    coordinates: [longitude, latitude],
                },
            };
        });

    const { clusters, supercluster } = useSupercluster({
        points: points || [],
        bounds,
        zoom,
        options: { radius: 10, maxZoom: 12 },
    });

    const getPaddedBounds = useCallback((map) => {
        const b = map.getBounds();
        const lngSpan = b.getEast() - b.getWest();
        const latSpan = b.getNorth() - b.getSouth();
        const pad = 0.6;
        return [
            b.getWest() - lngSpan * pad,
            b.getSouth() - latSpan * pad,
            b.getEast() + lngSpan * pad,
            b.getNorth() + latSpan * pad,
        ];
    }, []);

    const onMapMove = useCallback(
        (evt) => {
            setViewState(evt.viewState);
            setZoom(evt.viewState.zoom);

            const map = mapRef.current;
            if (map) {
                setBounds(getPaddedBounds(map));
            }
            setActiveMarker(null);
        },
        [getPaddedBounds]
    );

    const onMapLoad = useCallback(() => {
        const map = mapRef.current;
        if (map) {
            setBounds(getPaddedBounds(map));
        }
        setMapLoaded(true);
    }, [getPaddedBounds]);

    useEffect(() => {
        if (!mapLoaded || !mapRef.current || !companies || companies.length === 0) return;

        const validCompanies = companies.filter(
            (c) =>
                c.latitude &&
                c.longitude &&
                parseFloat(c.latitude) !== 0 &&
                parseFloat(c.longitude) !== 0
        );
        if (validCompanies.length === 0) return;

        const lngs = validCompanies.map((c) => parseFloat(c.longitude));
        const lats = validCompanies.map((c) => parseFloat(c.latitude));

        const minLng = Math.min(...lngs);
        const maxLng = Math.max(...lngs);
        const minLat = Math.min(...lats);
        const maxLat = Math.max(...lats);

        const timer = setTimeout(() => {
            if (!mapRef.current) return;

            if (validCompanies.length === 1) {
                mapRef.current.flyTo({
                    center: [lngs[0], lats[0]],
                    zoom: 13,
                    duration: 2000,
                });
            } else {
                mapRef.current.fitBounds(
                    [
                        [minLng, minLat],
                        [maxLng, maxLat],
                    ],
                    {
                        padding: 60,
                        duration: 2000,
                        maxZoom: 14,
                    }
                );
            }
        }, 800);

        return () => clearTimeout(timer);
    }, [mapLoaded, companies]);

    const handleMapClick = () => {
        setZoomEnabled(true);
        setShowZoomMsg(false);
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
    };

    const handleWheel = (e) => {
        if (!zoomEnabled) {
            setShowZoomMsg(true);
            e.preventDefault();
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            timeoutRef.current = setTimeout(() => {
                setShowZoomMsg(false);
            }, 2000);
        }
    };

    const handleMarkerClick = (locationId) => {
        setActiveMarker((prev) => (prev === locationId ? null : locationId));
    };

    const handleClusterClick = (cluster) => {
        const expansionZoom = Math.min(supercluster.getClusterExpansionZoom(cluster.id), 20);
        const [lng, lat] = cluster.geometry.coordinates;
        setViewState((prev) => ({
            ...prev,
            longitude: lng,
            latitude: lat,
            zoom: expansionZoom,
        }));
    };

    const openCompanyLocation = (props) => {
        const { companyDbId, locationId } = props;
        const href = companyLocationDeepLink(companyDbId, locationId);
        if (!href) return;
        navigate(href);
    };

    return (
        <div className={styles.mapWrapper} onClick={handleMapClick} onWheel={handleWheel}>
            {showZoomMsg && !zoomEnabled && (
                <div className={styles.zoomOverlay}>Smelltu á kortið til að geta þysjað inn</div>
            )}
            <Map
                ref={mapRef}
                {...viewState}
                onMove={onMapMove}
                onLoad={onMapLoad}
                onError={(e) => console.warn('Mapbox error:', e.error?.message)}
                style={{ width: '100%', height: '100%' }}
                mapStyle={isDark ? MAP_STYLE_DARK : MAP_STYLE_LIGHT}
                mapboxAccessToken={MAPBOX_TOKEN}
                projection="mercator"
                maxPitch={0}
                scrollZoom={zoomEnabled}
                dragPan={zoomEnabled}
                dragRotate={false}
                pitchWithRotate={false}
                touchZoomRotate={zoomEnabled}
                doubleClickZoom={zoomEnabled}
                keyboard={zoomEnabled}
            >
                <FullscreenControl position="top-right" />
                {clusters.map((cluster) => {
                    const [longitude, latitude] = cluster.geometry.coordinates;
                    const { cluster: isCluster, point_count: pointCount } = cluster.properties;

                    if (isCluster) {
                        const size = 20 + (pointCount / (points?.length || 1)) * 20;
                        return (
                            <Marker
                                key={`cluster-${cluster.id}`}
                                longitude={longitude}
                                latitude={latitude}
                                anchor="center"
                            >
                                <div
                                    className={styles.clusterMarker}
                                    style={{ width: `${size}px`, height: `${size}px` }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleClusterClick(cluster);
                                    }}
                                >
                                    {pointCount}
                                </div>
                            </Marker>
                        );
                    }

                    const {
                        locationId,
                        logo,
                        cName,
                        address,
                        locationName,
                        description,
                        phone,
                        latitude: lat,
                        longitude: lng,
                    } = cluster.properties;

                    const showLocationName =
                        locationName && locationName.trim() && locationName.trim() !== cName?.trim();

                    return (
                        <Marker
                            key={`marker-${locationId}`}
                            longitude={longitude}
                            latitude={latitude}
                            anchor="center"
                        >
                            <div className={styles.markerWrap}>
                                <button
                                    type="button"
                                    className={styles.markerButton}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleMarkerClick(locationId);
                                    }}
                                    aria-label={cName || locationName || t('address')}
                                >
                                    <div className={styles.markerInner}>
                                        <div
                                            className={styles.mImg}
                                            style={{
                                                backgroundImage: `url(${logo || markerIcon})`,
                                            }}
                                        />
                                    </div>
                                </button>
                                {activeMarker === locationId && (
                                    <div
                                        className={styles.infoWindow}
                                        role="link"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            openCompanyLocation(cluster.properties);
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                openCompanyLocation(cluster.properties);
                                            }
                                        }}
                                        tabIndex={0}
                                    >
                                        <div className={styles.infoWindowHeader}>
                                            {logo && (
                                                <div
                                                    className={styles.infoWindowLogo}
                                                    style={{
                                                        backgroundImage: `url(${logo})`,
                                                    }}
                                                />
                                            )}
                                            <div className={styles.infoWindowTitles}>
                                                {cName ? (
                                                    <div className={styles.infoWindowName}>
                                                        {cName}
                                                    </div>
                                                ) : null}
                                                {showLocationName ? (
                                                    <div className={styles.infoWindowLocationName}>
                                                        {locationName}
                                                    </div>
                                                ) : null}
                                            </div>
                                        </div>

                                        {address ? (
                                            <div className={styles.infoWindowRow}>
                                                <span className={styles.infoWindowLabel}>
                                                    {t('address')}
                                                </span>
                                                <span className={styles.infoWindowValue}>
                                                    {address}
                                                </span>
                                            </div>
                                        ) : null}

                                        {phone ? (
                                            <div className={styles.infoWindowRow}>
                                                <span className={styles.infoWindowLabel}>
                                                    {t('phone')}
                                                </span>
                                                <a
                                                    className={styles.infoWindowLink}
                                                    href={`tel:${phone}`}
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    {phone}
                                                </a>
                                            </div>
                                        ) : null}

                                        {description ? (
                                            <div className={styles.infoWindowDescription}>
                                                {description}
                                            </div>
                                        ) : null}

                                        <div className={styles.infoWindowCoords}>
                                            {Number(lat).toFixed(5)}, {Number(lng).toFixed(5)}
                                        </div>

                                        <div className={styles.infoWindowActions}>
                                            <span className={styles.infoWindowPrimaryAction}>
                                                {t('map.open_location')}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </Marker>
                    );
                })}
            </Map>
        </div>
    );
}
